import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

const useThirdPaneCallbacks = (
  prefix: string,
  itemIds: string[] | readonly string[] = [],
  currentId?: string,
  idField: string = 'id' as string
) => {
  const history = useHistory();

  const closeThirdPane = useCallback(
    (value: boolean) => {
      if (!value) {
        history.push(`${prefix}`);
      }
    },
    [history]
  );

  const itemIndex = currentId ? itemIds.indexOf(currentId) : undefined;

  const prevEnabled = typeof itemIndex === 'number' && itemIndex > 0;
  const prev = useCallback(() => {
    if (typeof itemIndex === 'number' && prevEnabled) {
      history.replace(`${prefix}/${itemIds[itemIndex - 1]}`);
    }
  }, [history, itemIds, itemIndex, prefix]);

  const nextEnabled =
    typeof itemIndex === 'number' && itemIndex + 1 < itemIds.length;
  const next = useCallback(() => {
    if (typeof itemIndex === 'number' && nextEnabled) {
      history.replace(`${prefix}/${itemIds[itemIndex + 1]}`);
    }
  }, [history, itemIds, nextEnabled, prefix, itemIndex]);

  const onRowClick = useCallback(
    (item: any) => {
      const itemId = item[idField];
      if (currentId) {
        if (currentId === itemId) {
          history.push(`${prefix}`);
        } else {
          history.replace(`${prefix}/${itemId}`);
        }
      } else {
        history.push(`${prefix}/${itemId}`);
      }
    },
    [history, prefix, idField, currentId]
  );

  const onArrowDown = useCallback(
    (e: KeyboardEvent) => {
      const target: any | null = e.target;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') {
        return;
      }
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          if (prevEnabled) {
            prev();
          }
          break;

        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          if (nextEnabled) {
            next();
          }
          break;

        case 'Escape':
          e.preventDefault();
          closeThirdPane(false);
          break;
      }
    },
    [nextEnabled, prevEnabled, next, prev]
  );

  useEffect(() => {
    window.addEventListener('keydown', onArrowDown);

    return () => window.removeEventListener('keydown', onArrowDown);
  }, [onArrowDown]);

  return {
    onRowClick,
    closeThirdPane,
    prev,
    prevEnabled,
    next,
    nextEnabled,
  };
};

export default useThirdPaneCallbacks;
